import { type FC, type PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { NotificationHubContext } from '@Contexts/notificationHub';
import { useHubMethod } from '@Contexts/signal';
import {
    useAcknowledgeAllNotifications,
    useAcknowledgeNotification,
    useNotificationsQuery,
    useUnreadNotificationsCount,
} from '@Hooks/Messages';
import { useGlobalActionMessageHandlers, useGlobalToastHandler } from '@Hooks/useGlobalMessageHandlers';
import { MessagingConstants } from '@Model';

export const NotificationHubProvider: FC<PropsWithChildren> = ({ children }) => {
    useGlobalToastHandler();
    useGlobalActionMessageHandlers();

    const [onlyUnread, setOnlyUnread] = useState(false);
    const { notifications, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage, resetToFirstPage } =
        useNotificationsQuery(onlyUnread);
    const { unreadCount, refetch: refetchUnreadCount } = useUnreadNotificationsCount();
    const { mutateAsync: acknowledgeMutation } = useAcknowledgeNotification();
    const { mutateAsync: acknowledgeAllMutation } = useAcknowledgeAllNotifications();

    const handleNotificationPush = useCallback(() => {
        void resetToFirstPage();
        void refetchUnreadCount();
    }, [resetToFirstPage, refetchUnreadCount]);

    useHubMethod(MessagingConstants.notification, handleNotificationPush);

    const value = useMemo(
        () => ({
            notifications,
            unreadCount,
            isLoading,
            onlyUnread,
            setOnlyUnread,
            hasNextPage,
            isFetchingNextPage,
            fetchNextPage,
            acknowledge: acknowledgeMutation,
            acknowledgeAll: acknowledgeAllMutation,
        }),
        [
            notifications,
            unreadCount,
            isLoading,
            onlyUnread,
            hasNextPage,
            isFetchingNextPage,
            fetchNextPage,
            acknowledgeMutation,
            acknowledgeAllMutation,
        ],
    );

    return <NotificationHubContext.Provider value={value}>{children}</NotificationHubContext.Provider>;
};
